import { Menu } from "@mantine/core";
import {
	CheckCircledIcon,
	Cross1Icon,
	DotsVerticalIcon,
} from "@modulz/radix-icons";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import { cacheKey } from "../../hooks/cacheStateKey";
import { useApproveRequest, useEnableRequest } from "../../hooks/useRequests";
import type { Request } from "../../services/requests-service";

const CellWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  cursor: pointer;
`;

export const RequestActionsCell = ({
	data,
}: { data: Request }): JSX.Element => {
	const { mutate: approve, isLoading: isApprovingRequest } =
		useApproveRequest();
	const { mutate: enable, isLoading: isRevokingRequest } = useEnableRequest();
	const queryClient = useQueryClient();

	const handleApproval = () => {
		approve(
			{
				requestId: data.id,
				enable: true,
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries([cacheKey.accessRequests]);
					queryClient.invalidateQueries([cacheKey.accessRequestStats]);
				},
			},
		);
	};

	const handleDecline = () => {
		approve(
			{
				requestId: data.id,
				enable: false,
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries([cacheKey.accessRequests]);
					queryClient.invalidateQueries([cacheKey.accessRequestStats]);
				},
			},
		);
	};

	const handleRevoke = () => {
		enable(
			{
				requestId: data.id,
				enable: false,
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries([cacheKey.accessRequests]);
					queryClient.invalidateQueries([cacheKey.accessRequestStats]);
				},
			},
		);
	};

	const handleUnRevoke = () => {
		enable(
			{
				requestId: data.id,
				enable: true,
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries([cacheKey.accessRequests]);
					queryClient.invalidateQueries([cacheKey.accessRequestStats]);
				},
			},
		);
	};

	const showApproveButton =
		data?.approvalStatus === "PENDING" ||
		data?.approvalStatus === "REVOKED" ||
		data?.approvalStatus === "DECLINED";
	const showDeclineButton = data?.approvalStatus === "PENDING";
	const showRevokeButton = data?.approvalStatus === "APPROVED";

	const isMakingRequest = isApprovingRequest || isRevokingRequest;
	return (
		<CellWrapper>
			<Menu
				style={{
					color: "var(--textColor)",
					backgroundColor: "var(--accentBg)",
          border: "var(--accentBg)"
				}}
				shadow="md"
				control={
					<div>
						<DotsVerticalIcon />
					</div>
				}
			>
				<Menu.Label>Request Actions</Menu.Label>
				{showApproveButton && (
					<Menu.Item
						icon={
							<CheckCircledIcon
								style={{
									color: "var(--textColor)",
									backgroundColor: "var(--accentBg)",
								}}
							/>
						}
						style={{
							color: "var(--textColor)",
							fontWeight: "bold",
							backgroundColor: "var(--accentBg)",
						}}
						disabled={isMakingRequest}
						onClick={
							data?.approvalStatus === "REVOKED"
								? handleUnRevoke
								: handleApproval
						}
					>
						Approve
					</Menu.Item>
				)}
				{showDeclineButton && (
					<Menu.Item
						icon={<Cross1Icon style={{ color: "#EC3D08" }} />}
						style={{ color: "#EC3D08", fontWeight: "bold" }}
						disabled={isMakingRequest}
						onClick={handleDecline}
					>
						Decline
					</Menu.Item>
				)}
				{showRevokeButton && (
					<Menu.Item
						icon={<Cross1Icon style={{ color: "#EC3D08" }} />}
						style={{ color: "#EC3D08", fontWeight: "bold" }}
						disabled={isMakingRequest}
						onClick={handleRevoke}
					>
						Revoke
					</Menu.Item>
				)}
			</Menu>
		</CellWrapper>
	);
};
